import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import {
  EmployeeV2ApiApiV2EmployeesPkEmployeeIdLocksDeleteRequest,
  useApi,
} from '../index';
import {
  EmployeeCacheKeys,
  HourCacheKeys,
  PunchCacheKeys,
  ScheduleCacheKeys,
  TimeCardCacheKeys,
} from './util/cache-keys';

export const UNDO_MUTATION_KEY = 'employeeUndo';

export const useEmployeeUndoStackGet = (employeeId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [EmployeeCacheKeys.EMPLOYEE_UNDO_STACK, { employeeId }],
    queryFn: () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdUndoStackGet({ pkEmployeeId: employeeId })
        .then((r) => r.data ?? { count: 0, actions: [] }),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(employeeId),
  });
};

const invalidateAfterUndo = (
  queryClient: ReturnType<typeof useQueryClient>,
  employeeId: number
) => {
  Promise.all([
    queryClient.invalidateQueries([
      EmployeeCacheKeys.EMPLOYEE_UNDO_STACK,
      { employeeId },
    ]),
    queryClient.invalidateQueries([HourCacheKeys.HOURS]),
    queryClient.invalidateQueries([HourCacheKeys.OTHER_HOURS]),
    queryClient.invalidateQueries([HourCacheKeys.DAILY_SUMMARY]),
    queryClient.invalidateQueries([HourCacheKeys.PAY_PERIOD_SUMMARY]),
    queryClient.invalidateQueries([PunchCacheKeys.EXCEPTIONS]),
    queryClient.invalidateQueries([ScheduleCacheKeys.EMPLOYEE_SCHEDULES]),
    queryClient.invalidateQueries([TimeCardCacheKeys.EMPLOYEE_TIME_CARD]),
    queryClient.invalidateQueries([EmployeeCacheKeys.EmployeeHeader]),
  ]).catch(noop);
};

export const useEmployeeReleaseLock = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();
  return useMutation(
    (params: EmployeeV2ApiApiV2EmployeesPkEmployeeIdLocksDeleteRequest) =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdLocksDelete(params)
        .then((r) => r.data),
    {
      onSuccess: async () => {
        await queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
        await queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
          .catch(noop);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useEmployeeReleaseLocksMine = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();
  return useMutation(
    () => api.employeeV2Api.apiV2EmployeesLocksMineDelete().then((r) => r.data),
    {
      onSuccess: async () => {
        await queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
        await queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
          .catch(noop);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useEmployeeReleaseAllLocks = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();
  return useMutation(
    () => api.employeeV2Api.apiV2EmployeesLocksDelete().then((r) => r.data),
    {
      onSuccess: async () => {
        await queryClient
          .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
          .catch(noop);
        await queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
          .catch(noop);
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useEmployeeUndoLast = (employeeId: number) => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();
  return useMutation(
    () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdUndoPost({ pkEmployeeId: employeeId })
        .then((r) => r.data),
    {
      mutationKey: [UNDO_MUTATION_KEY],
      onSuccess: () => {
        invalidateAfterUndo(queryClient, employeeId);
      },
      onError: (error: any) => {
        notifyError(getApiErrorMessage(error));
        if (error?.response?.status === 423) {
          queryClient
            .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
            .catch(noop);
          queryClient
            .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
            .catch(noop);
        }
      },
    }
  );
};

export const useEmployeeUndoAll = (employeeId: number) => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();
  return useMutation(
    () =>
      api.employeeV2Api
        .apiV2EmployeesPkEmployeeIdUndoAllPost({ pkEmployeeId: employeeId })
        .then((r) => r.data),
    {
      mutationKey: [UNDO_MUTATION_KEY],
      onSuccess: () => {
        invalidateAfterUndo(queryClient, employeeId);
      },
      onError: (error: any) => {
        notifyError(getApiErrorMessage(error));
        if (error?.response?.status === 423) {
          queryClient
            .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
            .catch(noop);
          queryClient
            .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
            .catch(noop);
        }
      },
    }
  );
};
